import { TUseTableProps, useTable } from "@/hooks/useTable";
import { MRT_Row, MRT_RowData, MRT_RowSelectionState } from "material-react-table";
import { Key, useMemo, useState } from "react";

type TUseSelectionTableProps<T extends MRT_RowData> = {
  primaryKey: keyof T;
  defaultSelectionKeys?: Key[];
  singleSelection?: boolean;
  enableRowSelection?: boolean | ((row: MRT_Row<T>) => boolean) | undefined;
  // 選択上限 (複数選択時のみ有効)。超える選択操作は反映せず onExceedMaxSelection を呼ぶ。
  maxSelection?: number;
  onExceedMaxSelection?: (max: number) => void;
} & TUseTableProps<T>;

export const useSelectionTable = <T extends MRT_RowData>({
  primaryKey,
  defaultSelectionKeys,
  singleSelection,
  enableRowSelection = true,
  maxSelection,
  onExceedMaxSelection,
  ...rest
}: TUseSelectionTableProps<T>) => {
  const formatSelectionKeys = (data: Key[]) => {
    return data.reduce((cur, item) => ({ ...cur, [item.toString()]: true }), {} as MRT_RowSelectionState);
  };

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(formatSelectionKeys(defaultSelectionKeys ?? []));

  const clearSelection = () => {
    setRowSelection({});
  };

  const removeRowSelectionById = (id: string | number) => {
    setRowSelection((cur) => {
      if (cur[id]) delete cur[id];
      return { ...cur };
    });
  };

  const rowSelectionKeys = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  const { table } = useTable({
    ...rest,
    enableRowSelection,
    getRowId: (row, index) => {
      // getRowId by primaryKey of the table
      if (primaryKey in row) {
        if (typeof row[primaryKey] === "string") return row[primaryKey];
        return JSON.stringify(row[primaryKey]);
      }
      return index.toString();
    },
    state: { rowSelection, ...rest.state },
    onRowSelectionChange: (newSelection) => {
      if (singleSelection) {
        const updatedSelection = typeof newSelection === "function" ? newSelection(rowSelection) : newSelection;
        const selectedKeys = Object.keys(updatedSelection);

        const newRowSelection: MRT_RowSelectionState = {};

        if (selectedKeys.length > 0) {
          const currentSelectedKey = Object.keys(rowSelection)[0];
          const selectedKey = selectedKeys.find((key) => key !== currentSelectedKey) || selectedKeys[0];
          newRowSelection[selectedKey] = true;
        }

        setRowSelection(newRowSelection);
      } else {
        const updated = typeof newSelection === "function" ? newSelection(rowSelection) : newSelection;
        // 上限を超える選択は反映せず通知する (現在の選択は維持)。
        if (maxSelection != null && Object.keys(updated).length > maxSelection) {
          onExceedMaxSelection?.(maxSelection);
          return;
        }
        setRowSelection(updated);
      }
    },
  });

  return { table, rowSelectionKeys, clearSelection, removeRowSelectionById, setRowSelection };
};
